import {
    answerHeightContentSignature,
    resolveHeightEstimationSource,
} from './answerHeightFingerprint'

export const THREAD_SESSION_MAX_SESSIONS = 12

export type ThreadPostDescriptor = {
    answerId: string
    geometryRevision: string
    position: number | null
    rowKey: string
}

export type ThreadSessionSnapshot = {
    revision: number
    rows: ThreadPostDescriptor[]
}

type ThreadSession = {
    lastAccessedAt: number
    snapshot: ThreadSessionSnapshot
}

const EMPTY_SNAPSHOT: ThreadSessionSnapshot = { revision: 0, rows: [] }
const sessions = new Map<string, ThreadSession>()
const listeners = new Map<string, Set<() => void>>()
let accessSequence = 0

const nextAccess = () => {
    accessSequence += 1
    return accessSequence
}

const descriptorEqual = (left: ThreadPostDescriptor, right: ThreadPostDescriptor) =>
    left.answerId === right.answerId &&
    left.rowKey === right.rowKey &&
    left.position === right.position &&
    left.geometryRevision === right.geometryRevision

const snapshotRowsEqual = (left: ThreadPostDescriptor[], right: ThreadPostDescriptor[]) =>
    left.length === right.length && left.every((row, index) => descriptorEqual(row, right[index]))

const notify = (threadKey: string) => listeners.get(threadKey)?.forEach(listener => listener())

function evictOldSessions(activeThreadKey?: string) {
    if (sessions.size <= THREAD_SESSION_MAX_SESSIONS) return

    const candidates = Array.from(sessions.entries())
        .filter(
            ([threadKey]) =>
                threadKey !== activeThreadKey && (listeners.get(threadKey)?.size || 0) === 0,
        )
        .sort(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt)

    while (sessions.size > THREAD_SESSION_MAX_SESSIONS && candidates.length > 0) {
        const [threadKey] = candidates.shift()!
        sessions.delete(threadKey)
        notify(threadKey)
    }
}

function geometryRevision(answer: any, previous?: ThreadPostDescriptor) {
    const source = resolveHeightEstimationSource(answer)
    return answerHeightContentSignature(source) || previous?.geometryRevision || 'unknown'
}

export function recordThreadSession(threadKey: string, answers: any[]): ThreadSessionSnapshot {
    if (!threadKey) return EMPTY_SNAPSHOT

    const previousSession = sessions.get(threadKey)
    const previousById = new Map(
        (previousSession?.snapshot.rows || []).map(descriptor => [descriptor.answerId, descriptor]),
    )
    const seen = new Set<string>()
    const rows: ThreadPostDescriptor[] = []

    for (const answer of answers || []) {
        if (answer?.id == null) continue
        const answerId = String(answer.id)
        if (!answerId || seen.has(answerId)) continue
        seen.add(answerId)
        const previous = previousById.get(answerId)
        const numericPosition = Number(answer.position)
        rows.push({
            answerId,
            geometryRevision: geometryRevision(answer, previous),
            position: Number.isFinite(numericPosition)
                ? numericPosition
                : previous?.position ?? null,
            rowKey: `thread-post:${answerId}`,
        })
    }

    const previousSnapshot = previousSession?.snapshot || EMPTY_SNAPSHOT
    const session =
        previousSession ||
        ({ lastAccessedAt: nextAccess(), snapshot: EMPTY_SNAPSHOT } as ThreadSession)
    session.lastAccessedAt = nextAccess()
    sessions.set(threadKey, session)
    evictOldSessions(threadKey)

    if (snapshotRowsEqual(previousSnapshot.rows, rows)) return previousSnapshot

    session.snapshot = {
        revision: previousSnapshot.revision + 1,
        rows,
    }
    notify(threadKey)
    return session.snapshot
}

export function updateThreadSessionGeometry(threadKey: string, answer: any) {
    if (!threadKey || answer?.id == null) return
    const session = sessions.get(threadKey)
    if (!session) return

    const answerId = String(answer.id)
    const rowIndex = session.snapshot.rows.findIndex(row => row.answerId === answerId)
    if (rowIndex < 0) return

    const currentRow = session.snapshot.rows[rowIndex]
    const nextGeometryRevision = geometryRevision(answer, currentRow)
    if (nextGeometryRevision === currentRow.geometryRevision) return

    const rows = session.snapshot.rows.slice()
    rows[rowIndex] = { ...currentRow, geometryRevision: nextGeometryRevision }
    session.snapshot = { revision: session.snapshot.revision + 1, rows }
    session.lastAccessedAt = nextAccess()
    notify(threadKey)
}

export function getThreadSessionSnapshot(threadKey: string | null | undefined) {
    if (!threadKey) return EMPTY_SNAPSHOT
    const session = sessions.get(threadKey)
    if (!session) return EMPTY_SNAPSHOT
    session.lastAccessedAt = nextAccess()
    return session.snapshot
}

export function subscribeToThreadSession(
    threadKey: string | null | undefined,
    listener: () => void,
) {
    if (!threadKey) return () => undefined
    let threadListeners = listeners.get(threadKey)
    if (!threadListeners) {
        threadListeners = new Set()
        listeners.set(threadKey, threadListeners)
    }
    threadListeners.add(listener)

    return () => {
        threadListeners?.delete(listener)
        if (threadListeners?.size === 0) {
            listeners.delete(threadKey)
            queueMicrotask(() => evictOldSessions())
        }
    }
}

export function resetThreadSessionsForTests() {
    sessions.clear()
    listeners.clear()
    accessSequence = 0
}
